import React from 'react'
import { ActivityRegistrationStatus } from '../../../../pure-js/types/GrooverTypes'
import { getGrooverText } from '../../libs/GrooverTextRepository'
import { Texts } from '../../../../pure-js/libs/Texts'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LockIcon from '@mui/icons-material/Lock'

export const WEEKDAYS = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag']

// Use strong, high-contrast colors for left border
export const STYLE_COLORS: Record<string, string> = {
  kizomba: '#388e3c', // strong green
  salsa: '#1976d2', // strong blue
  bachata: '#f57c00', // strong orange
  default: '#757575' // dark grey
}

export function getWeekdayIndex(date: string) {
  const jsDay = new Date(date).getDay()
  return jsDay === 0 ? 6 : jsDay - 1
}

export function timeToMinutes(time?: string) {
  if (!time) return 0
  const [h, m] = time.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

// Helper to darken a hex color
export function darkenColor(hex: string, amount = 0.18): string {
  let color = hex.replace('#', '')
  if (color.length === 3)
    color = color
      .split('')
      .map((c) => c + c)
      .join('')
  let num = parseInt(color, 16)
  let r = Math.max(0, ((num >> 16) & 0xff) * (1 - amount))
  let g = Math.max(0, ((num >> 8) & 0xff) * (1 - amount))
  let b = Math.max(0, (num & 0xff) * (1 - amount))
  return `#${((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b)).toString(16).slice(1)}`
}

export const getStatusIcon = (status?: ActivityRegistrationStatus) => {
  switch (status) {
    case ActivityRegistrationStatus.Full:
      return <ErrorOutlineIcon fontSize="inherit" style={{ marginRight: 4 }} />
    case ActivityRegistrationStatus.Closed:
      return <LockIcon fontSize="inherit" style={{ marginRight: 4 }} />
    case ActivityRegistrationStatus.ClosedForLeaders:
      return <LockIcon fontSize="inherit" style={{ marginRight: 4 }} />
    case ActivityRegistrationStatus.ClosedForFollowers:
      return <LockIcon fontSize="inherit" style={{ marginRight: 4 }} />
    case ActivityRegistrationStatus.Special:
      return <LockIcon fontSize="inherit" style={{ marginRight: 4 }} />

    default:
      return null
  }
}

export const getStatusText = (status?: ActivityRegistrationStatus) => {
  switch (status) {
    case ActivityRegistrationStatus.Full:
      return getGrooverText(Texts.adminGeneralEnumsEnrollmentStatusEnrollmentStatusFull)
    case ActivityRegistrationStatus.Closed:
      return getGrooverText(Texts.adminGeneralEnumsEnrollmentStatusEnrollmentStatusClosed)
    case ActivityRegistrationStatus.ClosedForLeaders:
      return getGrooverText(Texts.adminGeneralEnumsEnrollmentStatusEnrollmentStatusFullLeaders)
    case ActivityRegistrationStatus.ClosedForFollowers:
      return getGrooverText(Texts.adminGeneralEnumsEnrollmentStatusEnrollmentStatusAlmostFullFollower)
    case ActivityRegistrationStatus.Special:
      return getGrooverText(Texts.adminGeneralEnumsEnrollmentStatusEnrollmentStatusSpecial)
    default:
      return null
  }
}

export const getStatusBg = (status?: ActivityRegistrationStatus) => {
  switch (status) {
    case ActivityRegistrationStatus.Full:
      return '#e53935'
    case ActivityRegistrationStatus.Closed:
      return '#fbc02d'
    case ActivityRegistrationStatus.ClosedForLeaders:
      return '#1976d2'
    case ActivityRegistrationStatus.ClosedForFollowers:
      return '#f57c00'
    case ActivityRegistrationStatus.Special:
      return '#fbc02d'
    default:
      return 'rgba(0,0,0,0.18)'
  }
}

export const getStatusTextColor = (status?: ActivityRegistrationStatus) => {
  switch (status) {
    case ActivityRegistrationStatus.Closed:
    case ActivityRegistrationStatus.Special:
      return '#222'
    default:
      return '#fff'
  }
}

export const DROPIN_ONLY_STATUS_BG = '#607d8b'
export const DROPIN_ONLY_STATUS_TEXT_COLOR = '#fff'

export const getDropinOnlyStatusText = () => getGrooverText('activityListDropinOnly')

export type ActivityDisplayStatus = {
  text: string | null
  bg: string
  color: string
  icon: React.ReactNode | null
}

export const getActivityDisplayStatus = (activity: {
  dropinOnly?: boolean
  registrationStatus?: ActivityRegistrationStatus
}): ActivityDisplayStatus | null => {
  if (activity.dropinOnly) {
    return {
      text: getDropinOnlyStatusText(),
      bg: DROPIN_ONLY_STATUS_BG,
      color: DROPIN_ONLY_STATUS_TEXT_COLOR,
      icon: null
    }
  }

  if (!activity.registrationStatus || activity.registrationStatus === ActivityRegistrationStatus.Open) {
    return null
  }

  return {
    text: getStatusText(activity.registrationStatus),
    bg: getStatusBg(activity.registrationStatus),
    color: getStatusTextColor(activity.registrationStatus),
    icon: getStatusIcon(activity.registrationStatus)
  }
}

// Helper to check registration status robustly using the enum
export const isStatus = (status: any, value: any) => {
  if (!status || !value) return false
  return status === ActivityRegistrationStatus[value]
}

// Helper to determine if a color is light or dark
export function getContrastTextColor(bgColor: string): string {
  // Remove hash if present
  const color = bgColor.replace('#', '')
  // Parse r, g, b
  let r = 0,
    g = 0,
    b = 0
  if (color.length === 3) {
    r = parseInt(color[0] + color[0], 16)
    g = parseInt(color[1] + color[1], 16)
    b = parseInt(color[2] + color[2], 16)
  } else if (color.length === 6) {
    r = parseInt(color.substring(0, 2), 16)
    g = parseInt(color.substring(2, 4), 16)
    b = parseInt(color.substring(4, 6), 16)
  }
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#222' : '#fff'
}
